import React, { useEffect, useState } from 'react';
import { DatasetCard, DatasetRowProps } from '../../component/Dataset';
import CardGallery from '../../component/CardGallery';
import { useSearchParams } from 'react-router-dom';
import { FilterOption } from '../../component/FilterBar';
import FilterBar from '../../component/FilterBar';
import Loading from '../../component/Loading';
import { Button, Modal, Typography, Space, Alert, message } from 'antd';
import RenameDatasetModal from "../../component/RenameDatasetModal";
import TrainModelModal from '../../component/TrainNewModel';
import { createTrainingJob } from '../../pages/model-management/models';
import { renameDataset } from '../../component/Dataset';
import { DeleteOutlined, SelectOutlined, CloseOutlined } from '@ant-design/icons';

interface FilterOptions {
  // Adjust these fields based on your dataset filter needs
  categories: string[];
  formats: string[];
  sources: string[];
  tags: string[];
}

// Define a cache duration (e.g., 10 minutes)
const CACHE_DURATION_MS = 10 * 60 * 1000;

interface FilterCache {
  timestamp: number;
  data: FilterOptions;
}

interface SelectedItem {
  id: string;
  system_name: string;
}

const DatasetPage: React.FC = () => {
  const [trainOpen, setTrainOpen] = React.useState(false);
  const [trainTarget, setTrainTarget] = React.useState<DatasetRowProps | null>(null);
  const [trainLoading, setTrainLoading] = React.useState(false);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameTarget, setRenameTarget] = React.useState<DatasetRowProps | null>(null);
  const [renaming, setRenaming] = React.useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('limit') || '20', 10));
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterLoading, setFilterLoading] = useState(true);
  
  // Selection mode states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [sortBy, setSortBy]       = useState<string>(searchParams.get('sort_by') || 'timestamp');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>( (searchParams.get('sort_order') as any) || 'desc' );
  // State for filter options from the backend
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    categories: [],
    formats: [],
    sources: [],
    tags: []
  });

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    category: getArrayParamFromUrl('category'),
    format: getArrayParamFromUrl('format'),
    source: getArrayParamFromUrl('source'),
    tag: getArrayParamFromUrl('tag'),
    time_range: getArrayParamFromUrl('time_range'),
  });
  
  const [datasets, setDatasets] = useState<any[]>([]);

  // Helper function to get array parameters from URL
  function getArrayParamFromUrl(paramName: string): string[] {
    const params = searchParams.getAll(paramName);
    return params.length > 0 ? params : [];
  }

  // Update URL with current filters
  const updateUrlWithFilters = (page: number, limit: number, filterValues: Record<string, any>) => {
    const newParams = new URLSearchParams();
    
    // Add pagination params
    newParams.set('page', page.toString());
    newParams.set('limit', limit.toString());
    
    // Add filter params
    Object.entries(filterValues).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Clear previous values before adding new ones
        if (value.length > 0) {
          value.forEach(val => newParams.append(key, val));
        }
      } else if (value) {
        newParams.set(key, value);
      }
    });
    
    // Update the URL without reloading the page
    setSearchParams(newParams);
  };

  useEffect(() => {
    fetchDatasets(currentPage, pageSize, filters);
    // Try to load filters from cache first
    const cachedFilters = getFilterOptionsFromCache();
    if (cachedFilters) {
      setFilterOptions(cachedFilters);
      setFilterLoading(false);
    } else {
      fetchFilterOptions();
    }
  }, []);
  
  useEffect(() => {
    if (currentPage !== 1) {
      fetchDatasets(currentPage, pageSize, filters);
    }
  }, [currentPage]);

  const getFilterOptionsFromCache = (): FilterOptions | null => {
    try {
      const cachedData = localStorage.getItem('datasetFilterOptionsCache');
      if (cachedData) {
        const cache: FilterCache = JSON.parse(cachedData);
        const now = new Date().getTime();
        if (now - cache.timestamp < CACHE_DURATION_MS) {
          console.log('Using cached dataset filter options');
          return cache.data;
        } else {
          console.log('Cache expired, fetching new data');
        }
      }
    } catch (error) {
      console.error('Error reading from cache:', error);
    }
    return null;
  };

  const saveFilterOptionsToCache = (data: FilterOptions) => {
    try {
      const cacheData: FilterCache = {
        timestamp: new Date().getTime(),
        data: data
      };
      localStorage.setItem('datasetFilterOptionsCache', JSON.stringify(cacheData));
      console.log('Dataset filter options saved to cache');
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
  };

  const fetchFilterOptions = async () => {
    setFilterLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/dataset-filter-options`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch dataset filter options');
      }
      
      const data = await response.json();
      setFilterOptions(data);
      saveFilterOptionsToCache(data);
    } catch (error) {
      console.error('Error fetching dataset filter options:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | string[]) => {
    setFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchDatasets(1, pageSize, filters);
  };

  async function fetchDatasets(page: number, size: number, filterValues: Record<string, any>) {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: size.toString(),
      });
      
      // Append each filter to query parameters
      Object.entries(filterValues).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            // Append each value separately (e.g., ?category=TypeA&category=TypeB)
            value.forEach(val => queryParams.append(key, val));
          }
        } else if (value) {
          queryParams.append(key, value);
        }
      });

      // Update URL with the current filters
      updateUrlWithFilters(page, size, filterValues);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets?${queryParams.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch dataset data.');
      }
      const data = await response.json();
      console.log(data);
      setDatasets(data.items);
      setTotalPages(data.total_pages);
      setTotalItems(data.total_count);
    } catch (error) {
      console.error('Error fetching dataset data:', error);
    } finally {
      setLoading(false);
    }
  }

  // Selection mode handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedItems([]); // Clear selections when toggling mode
  };

  const handleItemSelect = (id: string, system_name: string) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item.id === id && item.system_name === system_name);
      if (exists) {
        // Remove from selection
        return prev.filter(item => !(item.id === id && item.system_name === system_name));
      } else {
        // Add to selection
        return [...prev, { id, system_name }];
      }
    });
  };

  const selectAllItems = () => {
    if (selectedItems.length === datasets.length) {
      // Deselect all
      setSelectedItems([]);
    } else {
      // Select all current page items
      const allItems = datasets.map(dataset => ({
        id: dataset.id.toString(),
        system_name: dataset.system_name || ''
      }));
      setSelectedItems(allItems);
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteModalVisible(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  const handleDeleteExecute = async () => {
    setDeleteLoading(true);
    try {
      // Prepare the dataset IDs for deletion
      const datasetIds = selectedItems.map(item => parseInt(item.id));
      console.log('Deleting datasets with IDs:', datasetIds);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/datasets/bulk-delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ dataset_ids: datasetIds })
      });

      if (!response.ok) {
        throw new Error('Failed to delete datasets');
      }

      const result = await response.json();
      
      // Show success message
      message.success(`Successfully deleted ${selectedItems.length} dataset(s)`);
      
      // Clear selections and exit selection mode
      setSelectedItems([]);
      setSelectionMode(false);
      
      // Refresh the datasets list
      await fetchDatasets(currentPage, pageSize, filters);
      
    } catch (error) {
      console.error('Error deleting datasets:', error);
      message.error('Failed to delete datasets. Please try again.');
    } finally {
      setDeleteLoading(false);
      setDeleteModalVisible(false);
    }
  };

  const openRename = (item: DatasetRowProps) => {
    setRenameTarget(item);
    setRenameOpen(true);
  };

  const handleRenameSubmit = async (newName: string) => {
    if (!renameTarget) return;
    try {
      setRenaming(true);
      const updated = await renameDataset(renameTarget.id, newName);

      // optimistic local update
      setDatasets((prev) =>
        prev.map((d) => (d.id === updated.id ? { ...d, name: updated.name, updatedAt: updated.updatedAt } : d))
      );

      message.success("Dataset renamed");
      setRenameOpen(false);
      setRenameTarget(null);
    } catch (e: any) {
      message.error(e.message || "Rename failed");
    } finally {
      setRenaming(false);
    }
  };

  const openTrain = (item?: DatasetRowProps) => {
    console.log("Opening train modal for item:", item);
  setTrainTarget(item ?? null);
  setTrainOpen(true);
};

const handleTrainSubmit = async (vals: any) => {
  try {
    setTrainLoading(true);
    const payload = {
      dataset_id: Number(vals.dataset_id),
      dataset_version_id: vals.dataset_version_id ? Number(vals.dataset_version_id) : undefined,
      model_name: String(vals.model_name),
      arch: String(vals.arch),
      epochs: Number(vals.epochs),
      batch_size: Number(vals.batch_size),
      learning_rate: Number(vals.learning_rate),
      augmentations: vals.augmentations || [],
      notes: vals.notes || "",
    };
    const out = await createTrainingJob(payload);
    message.success(`Training job #${out.job_id} queued`);
    setTrainOpen(false);
    setTrainTarget(null);
    // optional: navigate to a job page or refresh jobs list
  } catch (e: any) {
    message.error(e.message || "Failed to start training");
  } finally {
    setTrainLoading(false);
  }
};

  // Get selected dataset names for confirmation modal
  const getSelectedDatasetNames = () => {
    return selectedItems.map(selected => {
      const dataset = datasets.find(d => d.id.toString() === selected.id);
      return dataset?.name || `Dataset ${selected.id}`;
    });
  };

  const filterBarOptions: FilterOption[] = [
    {
      name: "category",
      label: "Category",
      type: "select",
      options: filterOptions.categories,
    },
    {
      name: "format",
      label: "Format",
      type: "select",
      options: filterOptions.formats,
    },
    {
      name: "source",
      label: "Source",
      type: "select",
      options: filterOptions.sources,
    },
    {
      name: "tag",
      label: "Tags",
      type: "select",
      options: filterOptions.tags,
    },
  ];

  return (
    <div className="flex h-screen gallery-container">
      <main className="flex-1 bg-gray-500 p-6">
        {loading && <Loading message="Loading Datasets..." />}
        
        {/* Action Bar */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              type={selectionMode ? "default" : "primary"}
              icon={selectionMode ? <CloseOutlined /> : <SelectOutlined />}
              onClick={toggleSelectionMode}
            >
              {selectionMode ? 'Cancel Selection' : 'Select Datasets'}
            </Button>
            
            {selectionMode && (
              <>
                <Button
                  onClick={selectAllItems}
                  disabled={datasets.length === 0}
                >
                  {selectedItems.length === datasets.length ? 'Deselect All' : 'Select All'}
                </Button>
                
                <span className="text-sm text-gray-600">
                  {selectedItems.length} of {datasets.length} selected
                </span>
              </>
            )}
          </div>
          
          {selectionMode && selectedItems.length > 0 && (
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
            >
              Delete Selected ({selectedItems.length})
            </Button>
          )}
        </div>

        <FilterBar
          filters={filters}
          filterOptions={filterBarOptions}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          fetchDatasets(currentPage, pageSize, filters);
        }}
          isLoading={filterLoading}
        />
        
        <CardGallery
           CardComponent={(props) => (
            <DatasetCard
              {...props}
              showActions={!selectionMode}
              onAction={(action, item) => {
                if (action === "train") openTrain(item as DatasetRowProps);
                if (action === "rename") openRename(item as DatasetRowProps);
                // handle other actions
              }}
            />
          )}
          cardClickPath='/dataset-management'
          data={datasets}
          loading={loading}
          selectionMode={selectionMode}
          selectedItems={selectedItems}
          onItemSelect={handleItemSelect}
          onPageChange={(page: number, size?: number) => {
            setCurrentPage(page);
            if (size && size !== pageSize) {
              setPageSize(size);
              fetchDatasets(1, size, filters);
            } else {
              fetchDatasets(page, pageSize, filters);
            }
          }}
          itemsPerPage={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
          currentPage={currentPage}
        />
        <RenameDatasetModal
          open={renameOpen}
          dataset={renameTarget ? { id: renameTarget.id, name: renameTarget.name } : null}
          confirmLoading={renaming}
          onCancel={() => { setRenameOpen(false); setRenameTarget(null); }}
          onSubmit={handleRenameSubmit}
        />
        <TrainModelModal
          open={trainOpen}
          initialDataset={trainTarget ? { id: trainTarget.id, name: trainTarget.name, dataset_version_id: trainTarget.dataset_version_id } : null}
          datasetOptions={datasets.map(d => ({ value: d.id, label: d.name }))}
          confirmLoading={trainLoading}
          onCancel={() => { setTrainOpen(false); setTrainTarget(null); }}
          onSubmit={handleTrainSubmit}
        />
        {/* Delete Confirmation Modal */}
        <Modal
          title="Confirm Deletion"
          open={deleteModalVisible}
          onOk={handleDeleteExecute}
          onCancel={handleDeleteCancel}
          okText="Delete"
          cancelText="Cancel"
          okType="danger"
          confirmLoading={deleteLoading}
          width={600}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Alert
              message="Warning"
              description="This action cannot be undone. The selected datasets will be permanently deleted."
              type="warning"
              showIcon
            />
            
            <div>
              <Typography.Text strong>
                You are about to delete {selectedItems.length} dataset(s):
              </Typography.Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {getSelectedDatasetNames().map((name, index) => (
                  <li key={index}>
                    <Typography.Text>{name}</Typography.Text>
                  </li>
                ))}
              </ul>
            </div>
          </Space>
        </Modal>
      </main>
    </div>
  );
};

export default DatasetPage;